import { Box, type BoxProps } from "@mui/material";
import { type ReactNode } from "react";

export interface TwoColumnGridProps {
  /**
   * The items to render in the grid
   */
  children: ReactNode[];
  /**
   * Additional props to pass to the container
   */
  sx?: BoxProps["sx"];
  /**
   * Gap between items (default: 1.5)
   */
  gap?: number | { sm: number; xs: number };
  /**
   * Whether to show single column layout (e.g. on mobile)
   */
  singleColumn?: boolean;
}

/**
 * A responsive two-column grid that maintains item order while filling space efficiently
 */
export const TwoColumnGrid = ({ children, sx, gap = { sm: 1.5, xs: 1 }, singleColumn = false }: TwoColumnGridProps) => {
  if (singleColumn) {
    return (
      <Box data-testid="two-column-grid" sx={{ display: "flex", flexDirection: "column", gap, width: "100%", ...sx }}>
        {children}
      </Box>
    );
  }

  // Use a single grid so items in the same row share equal height
  return (
    <Box
      data-testid="two-column-grid"
      sx={{
        display: "grid",
        gap,
        gridAutoFlow: "row",
        gridTemplateColumns: "1fr 1fr",
        width: "100%",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
